import React from 'react';
import {
    CallToActionPlaceholder,
    CssMasonry,
    ECB,
    FormInput,
    ProjectListCounter,
    ProjectListItemPlaceholder,
} from '@core/ui';
import { CallToAction } from '@core/frontend/components';
import { Link, NavLink } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import { IconSearch } from '@tabler/icons-react';
import { Helmet } from 'react-helmet';
import { useFundingStages, useProjectPageConfig } from '../../stores/index.js';
import { useProduct } from '../../shared/productContext.jsx';
import { useProjectListData } from './useProjectListData.js';
import styles from './ProjectList.module.scss';

/**
 * Projektliste.
 *
 * Alle Daten und die Filter-/Ranglogik stehen in `useProjectListData`; hier
 * steht nur die Darstellung. Produktunterschiede kommen aus dem Deskriptor:
 *
 * - `projectListView`: `'table'` (Bigfoot) oder `'grid'` (Cassiopeia). Der
 *   Listeneintrag selbst ist app-lokal und kommt aus
 *   `projectListComponents.ProjectListItem`; er bekommt einen Obermengen-Prop-Satz
 *   (`project`, `rank`, `voteCount`, `phases`, `fundingStages`, `projectBalance`,
 *   `projectPageConfig`, `achievements`) und nimmt sich daraus, was er braucht.
 * - `resolveVoteCount`: die produktspezifische Stimmenzahl-Regel der Listeneinträge.
 * - `projectListComponents.Banner`: optionaler Banner über der Liste
 *   (Zahlen-und-Marke in Cassiopeia). Fehlt er, rendert der Kern nichts.
 */
function ProjectList() {
    const product = useProduct();
    const {
        votingPhases,
        projectsCount,
        listItems,
        phrase,
        categoryOptions,
        categorySelected,
        cta,
        inputSelection,
        categoryHandler,
        onChangeHandler,
    } = useProjectListData();

    const fundingStages = useFundingStages();
    const projectPageConfig = useProjectPageConfig();

    const { ProjectListItem, Banner } = product.projectListComponents || {};
    const isGrid = product.projectListView === 'grid';

    const renderProjectEntry = (entry) => {
        const { project, rank, voteCount, projectBalance } = entry;
        const description = [
            rank && rank !== '-' ? `Platz ${rank}: ` : '',
            project.contestantName,
            project.projectTitle ? ` (${project.projectTitle})` : '',
            // `-1` ist der Marker des Kerns für "noch nicht öffentlich".
            ` mit ${voteCount >= 0 ? voteCount : '-'} Stimmen`,
            project.projectShortDescription ? `: ${project.projectShortDescription}` : '',
        ].join('');

        const listItem = (
            <ProjectListItem
                project={project}
                rank={rank}
                voteCount={voteCount}
                phases={votingPhases}
                fundingStages={fundingStages}
                projectBalance={projectBalance}
                projectPageConfig={projectPageConfig}
                achievements={project.achievements}
            />
        );

        if (isGrid) {
            return (
                <div className="d-flex anl__project-list--item h-100 mb-4" key={entry.key}>
                    <NavLink
                        className={`h-100 ${styles.ProjectListLink} flex-grow-1 anl_project-list--project-lnk text-decoration-none`}
                        to={`/projekte/${project._id}`}
                    >
                        {listItem}
                        <p className="visually-hidden">{description}</p>
                    </NavLink>
                </div>
            );
        }

        return (
            <Link
                className={`${styles.ProjectListLink} text-decoration-none anl_project-list--project-lnk`}
                to={'/projekte/' + project._id}
                key={entry.key}
            >
                {listItem}
                <p className="visually-hidden">{description}</p>
            </Link>
        );
    };

    const renderEcbEntry = (entry) => {
        const { ecb } = entry;

        if (isGrid) {
            return (
                <div key={entry.key} className="my-2">
                    <ECB
                        assets={ecb.assets}
                        altText={ecb.altText}
                        link={ecb.link}
                        index={ecb.order}
                        variant="card"
                        buttonOnImage={false}
                        defaultButtonTextColor="light"
                        defaultButtonPosition="fullwidth"
                        // Das Förder-Raster zählt die eTracker-Buttons historisch ab 0 (`anl_ecb--button-0`);
                        // das ist ein Tracking-Vertrag, keine Darstellungsvorliebe.
                        analyticsIndexBase={0}
                    />
                </div>
            );
        }

        return (
            <div key={entry.key} className={`${styles.ProjectListLink} border-bottom my-0`}>
                <ECB assets={ecb.assets} altText={ecb.altText} link={ecb.link} index={ecb.order} />
            </div>
        );
    };

    let listRender = (
        <>
            <ProjectListItemPlaceholder variant={product.projectListView} />
            <ProjectListItemPlaceholder variant={product.projectListView} />
            <ProjectListItemPlaceholder variant={product.projectListView} />
        </>
    );

    if (listItems && ProjectListItem) {
        listRender = listItems.map((entry) =>
            entry.type === 'ecb' ? renderEcbEntry(entry) : renderProjectEntry(entry)
        );
    }

    let categoryForm = null;
    if (categoryOptions) {
        categoryForm = (
            <FormInput
                wrapperCSSClass="category-select mb-3"
                elementType={categoryOptions.elementType}
                elementConfig={categoryOptions.elementConfig}
                value={categorySelected}
                changed={(event) => categoryHandler(event)}
                disabled={!inputSelection}
                analyticsClassName="anl_project-list--category-select"
                shouldValidate={categoryOptions.valid}
                helperText={`${categoryOptions.elementConfig.label}, wie z.B. ${categoryOptions.elementConfig.options[1]?.displayValue} oder ${categoryOptions.elementConfig.options[2]?.displayValue}`}
            />
        );
    }

    let ctaRender = <CallToActionPlaceholder />;

    if (cta) {
        ctaRender = (
            <CallToAction
                btnClass={`anl_cta--${cta.page}--btn-${cta.position}`}
                headline={cta.headline}
                copy={cta.copy}
                buttonText={cta.buttonText}
                buttonColor={cta.buttonColor}
                linkUrl={cta.linkUrl}
                outline={cta.outline}
                buttonVisibility={cta.buttonVisibility}
            />
        );
    }

    return (
        <>
            <Helmet>
                <title>{`Teilnehmende | ${process.env.REACT_APP_PROJECT_TITLE}`}</title>
            </Helmet>

            {Banner ? (
                <Row className="row-cols-1">
                    <Banner />
                </Row>
            ) : null}
            {/* CallToAction */}
            <Row className="my-3">
                <Col>{ctaRender}</Col>
            </Row>
            <Row className={styles.ProjectListCounterContainer + ' bg-light pt-3 p-0 border-top border-bottom'}>
                {categoryForm}
                <Col className="project-search">
                    <FormInput
                        disabled={!inputSelection}
                        changed={onChangeHandler}
                        inlineIcon={IconSearch}
                        analyticsClassName="anl_project-list--search-bar"
                        label="Teilnehmende suchen"
                        elementConfig={{
                            label: 'Teilnehmende suchen',
                            placeholder: 'Musterverein e.V.',
                            type: 'search',
                        }}
                        elementType="search"
                        helperText="Freitextsuche nach einem Teilnehmenden"
                        value={phrase}
                    />
                </Col>
            </Row>
            <Row className="my-3">
                <ProjectListCounter projectsCount={projectsCount} categories={categoryOptions} />
            </Row>
            <Row>
                <Col className="px-0 min-vh-100">{isGrid ? <CssMasonry>{listRender}</CssMasonry> : listRender}</Col>
            </Row>
        </>
    );
}

export default ProjectList;
